import Response from './response';
import * as fsFn from 'fs';
import * as urlFn from 'url';
import * as zlib from 'zlib';
import Cookies from './server.cookie';
import * as Http from 'http';

import * as Router from './router';

import initActiveServer, { server as activeServer } from './server.active';
import initStaticServer, { server as staticServer } from './server.static';
import initVirtualServer, { server as virtualServer } from './server.virtual';
import { Config, Render } from './index';
import { createInfo, ExtendedObject } from './info';
import { pathMatch } from './util.path-match';
import { Readable } from 'stream';

async function stat(path: string): Promise<fsFn.Stats> {
	return new Promise((resolve, reject) => fsFn.stat(path, (e, d) => e ? reject() : resolve(d)));
}

async function handle(ret: Response, req: Http.IncomingMessage, res: Http.ServerResponse, cache: boolean, cookie?: string[], render?: Render): Promise<boolean> {
	if (!ret) { return false; }
	const { download, transfer, filePath, renderFile, appId } = ret;
	let { statusCode, mime, body, stream, value } = ret;
	let gzip = true, length = 0;

	// 302跳转
	if (transfer) {
		res.statusCode = 302;
		res.setHeader('Location', transfer);
		if (cookie) { res.setHeader('Set-Cookie', cookie); }
		res.end();
		return true;
	}

	let stats!: fsFn.Stats;
	//获取信息
	if (filePath) {
		stats = await stat(filePath);
		if (!stats) { return false; }
	}
	//使用文件缓存
	if (cache && filePath) {
		let time = req.headers['if-modified-since'];
		if (time && new Date(time) > stats.mtime) {
			res.statusCode = 304;
			if (cookie) {
				res.setHeader('Set-Cookie', cookie);
			}
			res.end();
			return true;
		}
	}


	// 渲染
	if (render && renderFile) {
		try {
			const data = await render(typeof value ==='object' && value || {}, renderFile, appId);
			if (data instanceof Readable) {
				stream = data;
			} else {
				const buffer = data && (data as {buffer: ArrayBuffer | SharedArrayBuffer}).buffer;
				if(buffer instanceof ArrayBuffer || buffer instanceof SharedArrayBuffer) {
					body = buffer;
				} else {
					body = data as ArrayBuffer | SharedArrayBuffer | string | Buffer | undefined;
				}
			}
	
		} catch(e) {
			statusCode = 500;
			body = "";
			stream = undefined;
		}
	}
	//设置状态码与cookie
	res.statusCode = statusCode || 200;
	if (cookie) {
		res.setHeader('Set-Cookie', cookie);
	}

	//下载处理
	if (download && (filePath || stream)) {
		if (typeof download === 'string') {
			res.setHeader('Content-Disposition', `attachment; filename=${encodeURIComponent(download)}`);
		} else {
			res.setHeader('Content-Disposition', 'attachment');
		}
		if (filePath) {
			// TODO: res.download(filePath);
			// return true;			
		}
	} else if (download) {
		res.setHeader('Content-Disposition', 'attachment; filename=' + encodeURIComponent(download));
	}

	//文件转流
	if (filePath) {
		res.setHeader('Last-Modified', stats.mtime.toUTCString());
		stream = fsFn.createReadStream(filePath);
		if (stats.size < 1024 * 4) {
			length = stats.size;
			gzip = false;
		}
		if (!mime) {
			//TODO： 根据文件后缀获取mime
		}
	}
	if (mime) { res.setHeader('Content-Type', mime); }

	//判断是否使用gzip压缩
	gzip = gzip && ((req.headers['eccept-encoding'] as string) || '').replace(/\s/g, '').toLowerCase().split(',').includes('gzip');

	//发送body
	if (stream) {
		if (gzip) {
			stream = stream.pipe(zlib.createGzip());
		} else if (length) {
			res.setHeader('Content-Length', length);
		}
		stream.pipe(res);
	} else if (body) {
		body = Buffer.from(body as ArrayBuffer);
		if ((body as Buffer).length >= 1024 * 4 && gzip) {
			body = await new Promise(resolve => zlib.deflate(
				body as Buffer,
				(err, buffer) => resolve(err ? '' : buffer)
			));
		}
		res.setHeader('Content-Length', (body as Buffer).length);
		res.end(body);
	} else {
		res.end();
	}
	return true;
}

export interface Server {
	(req: Http.IncomingMessage, res: Http.ServerResponse, next?: () => any): Promise<void>;
	/** 静态服务器 */
	static: staticServer;
	/** 动态服务器 */
	active: activeServer;
	/** 虚拟服务器 */
	virtual: virtualServer;
}
function createServer<Ext extends ExtendedObject = {user: object}> (
	config: Config<Ext>,
	match: pathMatch,
	routerGet: Router.get<Ext>,
	info: createInfo<Ext>
): Server {
	const staticServer = initStaticServer(config, match);
	const activeServer = initActiveServer(config, match, routerGet, info);
	const virtualServer = initVirtualServer(activeServer, info);
	const server: Partial<Server> = async function server(req: Http.IncomingMessage, res: Http.ServerResponse, next?:() => any): Promise<void> {
		let beginTime = new Date;
		const { query, pathname: path = '/' } = urlFn.parse(req.url || '', true);
		let domain = req.headers.host;
		domain = domain ? domain.replace(/(?:\:\d*)?$/g, '') : '';
		//域名作为第一级路径
		const allpath = domain + path;
		const method = req.method || '';
		const headers = Object.assign({}, req.headers);
		config.log.link(method, allpath, headers);
		//静态路由，必须为GET请求且开启了静态服务器

		// 获取客户端IP
		const clientIp = [req.socket.remoteAddress || ''];
		clientIp.push(req.headers['x-real-ip'] as string);

		if (method === 'GET') {
			const ret = await staticServer(allpath, {
					method, domain, path, clientIp,
					getHeader(name: string) { return req.headers[name] },
					setHeader(name: string, value: string | number | string[]): void {return res.setHeader(name, value)},
					
				});
			if (ret && await handle(ret, req, res, true)) {
				config.log.handle(beginTime, res.statusCode, 'static', method, allpath, headers);
				return;
			}
		}
		{
			//解析Cookie
			const cookie = new Cookies(req.headers.cookie || '');
			// TODO: session

			const ret = await activeServer(
				allpath, query, req,
				{
					method, domain, path,
					getCookie: (...p) => cookie.get(...p),
					setCookie: (...p) => cookie.set(...p),
					clearCookie: (...p) => cookie.clear(...p),
					getHeader(name: string) { return req.headers[name] },
					setHeader(name: string, value: string | number | string[]): void {return res.setHeader(name, value)},
					clientIp,
				},
			);
			if (ret && await handle(ret, req, res, false, cookie.header.length ? cookie.header : undefined, config.render)) {
				ret.echoError(config.log.error);
				config.log.handle(beginTime, res.statusCode, 'active', method, allpath, headers);
				return;
			}

		}
		if (typeof next === 'function') {
			config.log.handle(beginTime, 0, 'next', method, allpath, headers);
			return next();
		}
		config.log.handle(beginTime, 404, 'none', method, allpath, headers);
		res.statusCode = 404;
		res.end('404');
	};

	server.static = staticServer;
	server.active = activeServer;
	server.virtual = virtualServer;
	return server as Server;

}
export default createServer;

